/**
 * Defensive number formatting helpers.
 *
 * Returns a consistent '—' placeholder instead of crashing on null/undefined/NaN.
 * Phase 4.8-B null-safety sweep — WQ-218
 */

export const fmt = (
  v: number | null | undefined,
  digits = 2,
  fallback = '—'
): string =>
  v == null || Number.isNaN(v) ? fallback : v.toFixed(digits);

export const fmtPct = (
  v: number | null | undefined,
  digits = 0,
  fallback = '—'
): string =>
  v == null || Number.isNaN(v) ? fallback : `${(v * 100).toFixed(digits)}%`;

/**
 * WQ-224: Defensive render helper for values crossing backend/frontend contract boundary.
 *
 * Returns the value if it's a React-renderable primitive; otherwise returns the
 * fallback and (in dev) warns to console. Use sparingly — primitive-typed fields
 * don't need this. Apply at sites where contract drift has been observed or where
 * the backend contract declares `dict` / `JSONB` that ships through to JSX.
 */
export function safeRender(v: unknown, fallback: string = '—'): string | number {
  if (v === null || v === undefined) return fallback;
  if (typeof v === 'string' || typeof v === 'number') return v;
  if (typeof v === 'boolean') return String(v);
  // Non-primitive (object, array, function) — not a valid React child
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.warn('[safeRender] Non-primitive passed to JSX render:', v);
  }
  return fallback;
}
